import React, { useCallback, useMemo, useState } from 'react'
import { RouteProps, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import AccountTemplate, { AccountTemplateProps } from '../../templates/Account'
import { useFooter } from '../../relay/Footer/useFooter'
import { useHeader } from '../../relay/Header/useHeader'
import { actions, selectors } from '../../redux'
import { ActionButtonLook, ActionButtonVariant } from '../../components/ActionButton/styled'
import { useSagaTakeLeading } from '../../hooks/useSaga'
import { router, routesPath } from '../../router'

const AccountPage: React.FC<RouteProps> = () => {
  const header = useHeader()
  const footer = useFooter()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const user = useSelector(selectors.auth.user)
  const [askChangePassword, setAskChangePassword] = useState<boolean>(false)

  useSagaTakeLeading(
    actions.auth.forgotSuccess,
    useCallback(() => {
      setAskChangePassword(true)
    }, [])
  )

  const templateProps: AccountTemplateProps = useMemo(
    () => ({
      layout: {
        header,
        footer,
      },
      banner: {
        title: t('account.banner.title'),
        headerButton: {
          text: t('account.banner.button.text'),
          variant: ActionButtonVariant.white,
          look: ActionButtonLook.outlined,
          onClick: () => {
            navigate(-1)
          },
          iconProps: {
            icon: 'arrowLeft',
          },
          iconPosition: 'left',
        },
      },
      identity: {
        title: t('account.identity.title'),
        firstName: t('account.identity.firstName', { firstName: user?.firstName }),
        lastName: t('account.identity.lastName', { lastName: user?.lastName }),
      },
      job: {
        title: t('account.role.title', { institution: user?.institution.name }),
        function: user?.declaration?.function?.name ?? '',
      },
      email: {
        title: t('account.email.title'),
        email: user?.email ?? '',
      },
      modify: {
        text: !askChangePassword ? t('account.modify.text') : t('account.modify.send'),
        variant: ActionButtonVariant.primary,
        look: ActionButtonLook.outlined,
        disabled: askChangePassword,
        onClick: () => {
          dispatch(actions.auth.forgotRequest({ email: user?.email ?? '' }))
        },
      },
      disconnect: {
        text: t('account.disconnect.text'),
        variant: ActionButtonVariant.danger,
        look: ActionButtonLook.outlined,
        onClick: () => {
          dispatch(actions.auth.logout())
        },
      },
      ...(user?.declaration?.first_created_at && {
        declarations: {
          text: t('account.declarations.text'),
          href: router(routesPath.userDeclarations),
        },
      }),
    }),
    [footer, header, user, t, navigate, dispatch, askChangePassword]
  )

  return <AccountTemplate {...templateProps} />
}

export default AccountPage
